import { api } from '../lib/api';
import type {
  CompleteVideoResponse,
  CompletedPart,
  CreateVideoBody,
  CreateVideoResponse,
  MorePartsResponse,
  OrgSummary,
  UploadPart,
  VideoDetail,
  VideoListResponse,
  VideoStatus,
  Visibility,
} from '../lib/types';
import type { UploadTransport } from '../recorder/uploader';
import { orgSharesLibrary } from './orgs';

export type LibrarySort = 'newest' | 'oldest' | 'views';

export interface LibraryQuery {
  /** Only what the person recorded, rather than everything in their organization too. */
  mine?: boolean;
  limit?: number;
  cursor?: string | null;
  /** Titles and transcripts. */
  q?: string;
  /** Recorded on or after, ISO. */
  since?: string | null;
  until?: string | null;
  sort?: LibrarySort;
  archived?: boolean;
}

export function libraryScopeKey(query: LibraryQuery): string {
  return [
    query.mine ? 'mine' : 'all',
    query.q ?? '',
    query.since ?? '',
    query.until ?? '',
    query.sort ?? 'newest',
    query.archived ? 'archived' : 'live',
  ].join(':');
}

function libraryPath(query: LibraryQuery): string {
  const params = new URLSearchParams();
  if (query.mine) params.set('mine', '1');
  if (query.limit) params.set('limit', String(query.limit));
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.q) params.set('q', query.q);
  if (query.since) params.set('since', query.since);
  if (query.until) params.set('until', query.until);
  if (query.sort && query.sort !== 'newest') params.set('sort', query.sort);
  if (query.archived) params.set('archived', '1');
  const search = params.toString();
  return `/v1/videos${search ? `?${search}` : ''}`;
}

export function fetchLibrary(query: LibraryQuery): Promise<VideoListResponse> {
  return api<VideoListResponse>(libraryPath(query));
}

export function fetchVideo(id: string): Promise<VideoDetail> {
  return api<VideoDetail>(`/v1/videos/${id}`);
}

export function updateVideo(
  id: string,
  body: { title?: string; summary?: string | null; hiddenLinks?: string[]; visibility?: Visibility },
): Promise<VideoDetail> {
  return api<VideoDetail>(`/v1/videos/${id}`, { method: 'PATCH', body });
}

/**
 * Who watched, for the recording's owner.
 *
 * Three kinds, because there are three kinds of viewer: people who were
 * signed in and can be named, addresses typed at the email gate, and
 * everyone else, who stays a number.
 */
export interface VideoViewers {
  people: Array<{
    id: string;
    name: string | null;
    email: string;
    avatarUrl: string | null;
    /** How many times they opened it; the figures below are their best run. */
    views: number;
    firstSeenAt: string;
    lastSeenAt: string;
    maxProgressSec: number;
    completed: boolean;
  }>;
  /** One row per browser we can tell apart, newest first. */
  anonymous: Array<{
    id: string;
    views: number;
    firstSeenAt: string;
    lastSeenAt: string;
    maxProgressSec: number;
    completed: boolean;
  }>;
  emails: Array<{ email: string; createdAt: string }>;
}

export function fetchViewers(videoId: string): Promise<VideoViewers> {
  return api<VideoViewers>(`/v1/videos/${videoId}/viewers`);
}

/**
 * A recording's share list: the addresses it is open to while its privacy is
 * "only people you name". Only its owner (and their team's admins, for a
 * recording the team can see) may read or write it.
 */
export interface VideoShares {
  emails: string[];
  max: number;
}

export function fetchVideoShares(id: string): Promise<VideoShares> {
  return api<VideoShares>(`/v1/videos/${id}/shares`);
}

/**
 * Replace the list. Whoever is new to it gets an email with the link, unless
 * `notify` says otherwise; nobody already on it is emailed again.
 */
export function setVideoShares(
  id: string,
  emails: string[],
  notify = true,
): Promise<VideoShares & { added: string[] }> {
  return api<VideoShares & { added: string[] }>(`/v1/videos/${id}/shares`, {
    method: 'PUT',
    body: { emails, notify },
  });
}

export function deleteVideo(id: string): Promise<void> {
  return api<void>(`/v1/videos/${id}`, { method: 'DELETE' });
}

export function restoreVideo(id: string): Promise<{ ok: boolean }> {
  return api(`/v1/videos/${id}/restore`, { method: 'POST' });
}

/**
 * May this person delete or rename this recording?
 *
 * Mirrors `canManageVideo` in the API's policy module, which is the
 * authority: its owner, or an admin or owner of the team it was recorded in,
 * while that team still shares a library. This copy exists so the library
 * never offers a control that the API would answer with a 404. Everyone else
 * on a team can see a teammate's recording and can play it; only these two
 * can put it in the archive.
 */
export function canManageVideo(
  video: { ownerId: string | null; orgId: string | null; deletedAt?: string | null },
  viewer: { userId: string | null; org: OrgSummary | null },
): boolean {
  if (video.deletedAt) return false;
  if (!viewer.userId) return false;
  if (video.ownerId !== null && video.ownerId === viewer.userId) return true;
  const org = viewer.org;
  if (!org || video.orgId === null || video.orgId !== org.id) return false;
  return (org.role === 'admin' || org.role === 'owner') && orgSharesLibrary(org.subscriptionStatus);
}

/** Copy for a status badge. Written to be read by a person, not a machine. */
export function statusLabel(status: VideoStatus): string {
  switch (status) {
    case 'uploading':
      return 'Uploading';
    case 'processing':
      return 'Processing';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

/**
 * The recorder's upload adapter.
 *
 * Pass a `requestToken` on /r/:token so every call carries the anonymous
 * session plus the link token, which is what makes slate-api file the upload
 * in the requester's library instead of rejecting it.
 */
export function createUploadTransport(requestToken?: string): UploadTransport {
  const extra = requestToken ? { requestToken, auth: 'optional' as const } : {};

  return {
    createVideo(body: CreateVideoBody): Promise<CreateVideoResponse> {
      return api<CreateVideoResponse>('/v1/videos', { method: 'POST', body, ...extra });
    },
    async moreParts(videoId: string, fromPartNumber: number, count: number): Promise<UploadPart[]> {
      const response = await api<MorePartsResponse>(`/v1/videos/${videoId}/parts`, {
        method: 'POST',
        body: { fromPartNumber, count },
        ...extra,
      });
      return response.parts;
    },
    complete(
      videoId: string,
      body: { parts: CompletedPart[]; durationSec?: number; title?: string },
    ): Promise<CompleteVideoResponse> {
      return api<CompleteVideoResponse>(`/v1/videos/${videoId}/complete`, {
        method: 'POST',
        body,
        ...extra,
      });
    },
    discard(videoId: string): Promise<void> {
      return api<void>(`/v1/videos/${videoId}`, { method: 'DELETE', ...extra });
    },
  };
}
